import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import Modal from "../../../components/Modal";
import { useAcademyCourses } from "../hooks/useAcademyCourses";
import { useAcademyProgress } from "../hooks/useAcademyProgress";
import { useAcademyCourseDetail } from "../hooks/useAcademyCourseDetail";
import { useCompleteVideo } from "../hooks/useCompleteVideo";
import { useEnrollCourse } from "../hooks/useEnrollCourse";
import { addTenantTopic } from "../../quiz/services/tenantTopicsService";
import { getTenantId } from "../../../utils/tenant";
import { useUser } from "../../../hooks/useUser";
import { useDeviceType } from "../../../hooks/useMediaQuery";
import playCircleIcon from "../../../assets/play-circle.png";

const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
};

const AcademyHome = () => {
    const [view, setView] = useState("list"); // 'list' | 'detail' | 'player'
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [showCongrats, setShowCongrats] = useState(false);
    const [completedCourseTitle, setCompletedCourseTitle] = useState("");
    const [showBackButton, setShowBackButton] = useState(false);
    const [showNextButton, setShowNextButton] = useState(false);
    const [redeemingTrivia, setRedeemingTrivia] = useState(false);
    const [enrollingCourseId, setEnrollingCourseId] = useState(null);
    const videoRef = useRef(null);
    const youtubePlayerRef = useRef(null);
    const handleVideoEndedRef = useRef(() => {});

    const { courses, loading: loadingCourses, error: errorCourses, reload: reloadCourses } = useAcademyCourses();
    const { progress, loading: loadingProgress, error: errorProgress, reload: reloadProgress } = useAcademyProgress();
    const { course: courseDetail, loading: loadingDetail, error: errorDetail } = useAcademyCourseDetail({
        courseId: selectedCourseId,
        enabled: view === "detail" || view === "player",
    });
    const routerHistory = useHistory();
    const { isHost, isOwner } = useUser();
    const { isMobile } = useDeviceType();

    const { complete, loading: completing } = useCompleteVideo();
    const { enroll } = useEnrollCourse();

    const progressByCourseId = useMemo(() => {
        const map = {};
        progress.forEach((p) => {
            const cid = typeof p.courseId === "object" ? p.courseId?._id : p.courseId;
            if (cid) map[cid] = p;
        });
        return map;
    }, [progress]);

    const enrolledCourseIds = useMemo(() => {
        const ids = new Set();
        progress.forEach((p) => {
            const cid = typeof p.courseId === "object" ? p.courseId?._id : p.courseId;
            if (cid) ids.add(String(cid));
        });
        return ids;
    }, [progress]);

    const handleSelectCourse = async (courseId) => {
        if (!enrolledCourseIds.has(String(courseId))) {
            setEnrollingCourseId(courseId);
            try {
                await enroll(courseId);
                await reloadProgress();
            } catch (err) {
                console.error("Error inscribiendo al curso:", err);
                setEnrollingCourseId(null);
                return;
            } finally {
                setEnrollingCourseId(null);
            }
        }
        setSelectedCourseId(courseId);
        setView("detail");
    };

    const getQuizTabForUser = () => {
        if (isHost) return "host-quiz";
        if (isOwner) return isMobile ? "resto-quiz" : "trivias";
        return "trivias";
    };

    const handleRedeemTrivia = async () => {
        const quizId = courseDetail?.quizId;
        console.log("[Academy] handleRedeemTrivia quizId:", quizId, "courseDetail:", courseDetail);
        if (!quizId) {
            routerHistory.push({ pathname: "/", search: `?tab=${getQuizTabForUser()}` });
            return;
        }
        setRedeemingTrivia(true);
        try {
            const tenantId = getTenantId();
            if (tenantId) {
                await addTenantTopic(tenantId, quizId);
                console.log("[Academy] addTenantTopic success");
            }
        } catch (err) {
            console.error("[Academy] Error canjeando trivia:", err);
        } finally {
            setRedeemingTrivia(false);
            localStorage.setItem("pendingQuizId", quizId);
            console.log("[Academy] saved pendingQuizId:", quizId, "navigating to tab:", getQuizTabForUser());
            routerHistory.push({ pathname: "/", search: `?tab=${getQuizTabForUser()}` });
        }
    };

    const handleBack = () => {
        if (view === "player") {
            setView("detail");
            setSelectedVideo(null);
            setShowBackButton(false);
            setShowNextButton(false);
        } else {
            setView("list");
            setSelectedCourseId(null);
        }
    };

    const handleVideoEnded = useCallback(async () => {
        if (!selectedCourseId || !selectedVideo?._id) return;
        try {
            const result = await complete(selectedCourseId, selectedVideo._id);
            if (result?.justCompleted) {
                setCompletedCourseTitle(courseDetail?.title || "");
                setShowCongrats(true);
                setShowBackButton(false);
                setShowNextButton(false);
            } else {
                setShowBackButton(true);
                const currentIndex = courseDetail?.videos?.findIndex((v) => v._id === selectedVideo._id);
                const hasNext = currentIndex !== -1 && currentIndex < (courseDetail?.videos?.length || 0) - 1;
                setShowNextButton(hasNext);
            }
            await reloadProgress();
        } catch (err) {
            console.error("Error completando video:", err);
        }
    }, [selectedCourseId, selectedVideo, complete, reloadProgress, courseDetail]);

    // Ref para que el player de YouTube siempre llame a la versión más reciente
    useEffect(() => {
        handleVideoEndedRef.current = handleVideoEnded;
    }, [handleVideoEnded]);

    // Inicializar player de YouTube cuando sea necesario
    useEffect(() => {
        if (view !== "player" || !selectedVideo || selectedVideo.type !== "youtube") return;
        const youtubeId = getYoutubeVideoId(selectedVideo.url);
        if (!youtubeId) return;

        const initPlayer = () => {
            if (!window.YT || !window.YT.Player) return;
            if (youtubePlayerRef.current) {
                youtubePlayerRef.current.destroy();
                youtubePlayerRef.current = null;
            }
            const player = new window.YT.Player("youtube-player", {
                videoId: youtubeId,
                playerVars: {
                    rel: 0,
                    enablejsapi: 1,
                },
                events: {
                    onStateChange: (event) => {
                        if (event.data === window.YT.PlayerState.ENDED) {
                            handleVideoEndedRef.current?.();
                        }
                    },
                },
            });
            youtubePlayerRef.current = player;
        };

        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            const existingScript = document.getElementById("youtube-iframe-api");
            if (!existingScript) {
                const tag = document.createElement("script");
                tag.id = "youtube-iframe-api";
                tag.src = "https://www.youtube.com/iframe_api";
                const firstScriptTag = document.getElementsByTagName("script")[0];
                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
            }
            const originalReady = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (originalReady) originalReady();
                initPlayer();
            };
            return () => {
                window.onYouTubeIframeAPIReady = originalReady;
            };
        }

        return () => {
            if (youtubePlayerRef.current) {
                youtubePlayerRef.current.destroy();
                youtubePlayerRef.current = null;
            }
        };
    }, [view, selectedVideo]);

    const openPlayer = useCallback((video) => {
        setSelectedVideo(video);
        setShowBackButton(false);
        setShowNextButton(false);
        setView("player");
    }, []);

    const handleNextVideo = useCallback(() => {
        if (!courseDetail?.videos || !selectedVideo) return;
        const currentIndex = courseDetail.videos.findIndex((v) => v._id === selectedVideo._id);
        const nextVideo = courseDetail.videos[currentIndex + 1];
        if (nextVideo) {
            openPlayer(nextVideo);
        }
    }, [courseDetail, selectedVideo, openPlayer]);

    const isVideoCompleted = (courseId, videoId) => {
        const p = progressByCourseId[courseId];
        if (!p || !Array.isArray(p.videos)) return false;
        return p.videos.some((v) => v.videoId === videoId && v.completed);
    };

    // Render: Lista de cursos (todos los disponibles)
    if (view === "list") {
        const enrolledList = progress
            .filter((p) => {
                const course = typeof p.courseId === "object" ? p.courseId : null;
                if (!course) return false;
                if (loadingCourses || courses.length === 0) return true;
                return courses.some((c) => String(c._id) === String(course._id));
            })
            .map((p) => {
                const course = typeof p.courseId === "object" ? p.courseId : null;
                return { progress: p, course };
            })
            .filter((item) => item.course);

        const availableList = loadingCourses
            ? []
            : courses
                  .filter((c) => !enrolledCourseIds.has(String(c._id)))
                  .map((course) => ({ course }));

        return (
            <Page>
                <Content>
                    <SectionTitle>Mis cursos</SectionTitle>
                    {loadingProgress && <StatusText>Cargando progreso...</StatusText>}
                    {errorProgress && <StatusText $error>{errorProgress}</StatusText>}
                    {!loadingProgress && !errorProgress && enrolledList.length === 0 && (
                        <StatusText>Aún no has empezado ningún curso.</StatusText>
                    )}
                    <Grid>
                        {enrolledList.map(({ progress: p, course }) => (
                            <Card
                                key={p._id}
                                onClick={() => handleSelectCourse(course._id)}
                                style={{ opacity: enrollingCourseId === course._id ? 0.6 : 1 }}
                            >
                                {course.thumbnail ? (
                                    <CardImage src={course.thumbnail} alt={course.title} />
                                ) : (
                                    <CardPlaceholder>
                                        <PlaceholderImage src={playCircleIcon} alt="Curso" />
                                    </CardPlaceholder>
                                )}
                                <CardBody>
                                    <CardTitle>{course.title}</CardTitle>
                                    <ProgressBar>
                                        <ProgressFill style={{ width: `${p.percentage || 0}%` }} />
                                    </ProgressBar>
                                    <ProgressText>{p.percentage || 0}% completado</ProgressText>
                                </CardBody>
                            </Card>
                        ))}
                    </Grid>

                    <SectionTitle style={{ marginTop: "2rem" }}>Cursos disponibles</SectionTitle>
                    {loadingCourses && <StatusText>Cargando cursos...</StatusText>}
                    {errorCourses && <StatusText $error>{errorCourses}</StatusText>}
                    {!loadingCourses && !errorCourses && availableList.length === 0 && (
                        <StatusText>No hay cursos disponibles por el momento.</StatusText>
                    )}
                    <Grid>
                        {availableList.map(({ course }) => (
                            <Card
                                key={course._id}
                                onClick={() => handleSelectCourse(course._id)}
                                style={{ opacity: enrollingCourseId === course._id ? 0.6 : 1 }}
                            >
                                {course.thumbnail ? (
                                    <CardImage src={course.thumbnail} alt={course.title} />
                                ) : (
                                    <CardPlaceholder>
                                        <PlaceholderImage src={playCircleIcon} alt="Curso" />
                                    </CardPlaceholder>
                                )}
                                <CardBody>
                                    <CardTitle>{course.title}</CardTitle>
                                    <ProgressBar>
                                        <ProgressFill style={{ width: "0%" }} />
                                    </ProgressBar>
                                    <ProgressText>No iniciado</ProgressText>
                                </CardBody>
                            </Card>
                        ))}
                    </Grid>
                </Content>
            </Page>
        );
    }

    // Render: Detalle del curso
    if (view === "detail" && courseDetail) {
        return (
            <Page>
                <Content>
                    <BackButton onClick={handleBack}>← Volver</BackButton>
                    <DetailHeader>
                        {courseDetail.thumbnail ? (
                            <DetailImage src={courseDetail.thumbnail} alt={courseDetail.title} />
                        ) : (
                            <DetailPlaceholder>
                                <PlaceholderImage src={playCircleIcon} alt="Curso" />
                            </DetailPlaceholder>
                        )}
                        <DetailInfo>
                            <DetailTitle>{courseDetail.title}</DetailTitle>
                            {courseDetail.description && <DetailDescription>{courseDetail.description}</DetailDescription>}
                            {courseDetail.category && <DetailCategory>Categoría: {courseDetail.category}</DetailCategory>}
                        </DetailInfo>
                    </DetailHeader>

                    <SectionTitle>Videos del curso</SectionTitle>
                    {loadingDetail && <StatusText>Cargando videos...</StatusText>}
                    {errorDetail && <StatusText $error>{errorDetail}</StatusText>}
                    <VideoList>
                        {(courseDetail.videos || []).map((video, idx) => {
                            const completed = isVideoCompleted(courseDetail._id, video._id);
                            return (
                                <VideoItem key={video._id} onClick={() => openPlayer(video)}>
                                    <VideoNumber>{idx + 1}</VideoNumber>
                                    <VideoInfo>
                                        <VideoTitle $completed={completed}>
                                            {completed ? "✓ " : ""}
                                            {video.title}
                                        </VideoTitle>
                                        {video.description && <VideoDesc>{video.description}</VideoDesc>}
                                        <VideoMeta>
                                            {video.type === "youtube" ? "YouTube" : "Video"}
                                            {video.duration ? ` • ${Math.floor(video.duration / 60)}:${String(video.duration % 60).padStart(2, "0")}` : ""}
                                        </VideoMeta>
                                    </VideoInfo>
                                    <PlayIcon>▶</PlayIcon>
                                </VideoItem>
                            );
                        })}
                    </VideoList>

                    {/* Trivia de evaluación oculta temporalmente
                    {courseDetail?.quizId && (
                        <TriviaSection>
                            <SectionTitle>Trivia de evaluación</SectionTitle>
                            {(() => {
                                const courseProgress = progressByCourseId[courseDetail._id];
                                const isCompleted = courseProgress?.percentage === 100 || courseProgress?.completed;
                                if (!isCompleted) {
                                    return (
                                        <TriviaCardLocked>
                                            <TriviaIcon>🔒</TriviaIcon>
                                            <TriviaCardText>
                                                <strong>Trivia bloqueada</strong>
                                                <span>Completa el curso al 100% para desbloquear la trivia de evaluación.</span>
                                            </TriviaCardText>
                                        </TriviaCardLocked>
                                    );
                                }
                                return (
                                    <TriviaCardUnlocked onClick={handleRedeemTrivia}>
                                        <TriviaIcon>📝</TriviaIcon>
                                        <TriviaCardText>
                                            <strong>Trivia de evaluación</strong>
                                            <span>{redeemingTrivia ? "Canjeando trivia..." : "Pon a prueba lo aprendido en este curso."}</span>
                                        </TriviaCardText>
                                        <TriviaArrow>{redeemingTrivia ? "⏳" : "›"}</TriviaArrow>
                                    </TriviaCardUnlocked>
                                );
                            })()}
                        </TriviaSection>
                    )}
                    */}
                </Content>
            </Page>
        );
    }

    // Render: Reproductor
    if (view === "player" && selectedVideo) {
        const youtubeId = selectedVideo.type === "youtube" ? getYoutubeVideoId(selectedVideo.url) : null;

        return (
            <Page>
                <Content>
                    <BackButton onClick={handleBack}>← Volver al curso</BackButton>
                    <PlayerTitle>{selectedVideo.title}</PlayerTitle>
                    <PlayerWrapper>
                        {youtubeId ? (
                            <YouTubePlayerContainer id="youtube-player" />
                        ) : (
                            <VideoPlayer
                                ref={videoRef}
                                src={selectedVideo.url}
                                controls
                                playsInline
                                onEnded={handleVideoEnded}
                            />
                        )}
                    </PlayerWrapper>
                    {youtubeId && completing && (
                        <ManualComplete>
                            <StatusText>Marcando como visto...</StatusText>
                        </ManualComplete>
                    )}
                    {(showBackButton || showNextButton) && !showCongrats && (
                        <PlayerActions>
                            {showBackButton && (
                                <ActionButton onClick={handleBack}>
                                    ← Volver atrás
                                </ActionButton>
                            )}
                            {showNextButton && (
                                <ActionButton onClick={handleNextVideo}>
                                    Siguiente video →
                                </ActionButton>
                            )}
                        </PlayerActions>
                    )}
                    {selectedVideo.description && (
                        <VideoDescription>{selectedVideo.description}</VideoDescription>
                    )}
                </Content>

                <Modal
                    isOpen={showCongrats}
                    onClose={() => { setShowCongrats(false); handleBack(); }}
                    onCancel={() => { setShowCongrats(false); handleBack(); }}
                    label="¡Felicidades!"
                    cancelButtonText="Volver atrás"
                    cancelButton={true}
                    acceptButton={false}
                >
                    <ModalMessage>
                        Has completado el curso <strong>"{completedCourseTitle}"</strong>.
                    </ModalMessage>
                </Modal>
            </Page>
        );
    }

    return (
        <Page>
            <Content>
                <StatusText>Cargando...</StatusText>
            </Content>
        </Page>
    );
};

export default AcademyHome;

/* ========== ESTILOS ========== */

const Page = styled.section`
  min-height: calc(100vh - 140px);
  padding: 12px 0 90px;

  @media (max-width: 768px) {
    padding: 12px 0 80px;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 0 15px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 16px 0 12px;
  color: #111827;
`;

const StatusText = styled.p`
  margin: 12px 0;
  font-size: 14px;
  color: ${(props) => (props.$error ? "#dc2626" : "#6b7280")};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  display: block;
`;

const CardPlaceholder = styled.div`
  width: 100%;
  height: 140px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlaceholderText = styled.span`
  font-size: ${(props) => (props.large ? "48px" : "32px")};
  font-weight: 700;
  color: #d1d5db;
`;

const PlaceholderImage = styled.img`
  width: 48px;
  height: 48px;
  opacity: 0.35;
`;

const CardBody = styled.div`
  padding: 12px;
`;

const CardTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #111827;
`;

const CardCategory = styled.span`
  font-size: 12px;
  color: #6b7280;
  text-transform: capitalize;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  margin-top: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #10b981;
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const ProgressText = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DetailImage = styled.img`
  width: 200px;
  height: 130px;
  object-fit: cover;
  border-radius: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    height: 180px;
  }
`;

const DetailPlaceholder = styled.div`
  width: 200px;
  height: 130px;
  background: #f3f4f6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    height: 180px;
  }
`;

const DetailInfo = styled.div`
  flex: 1;
`;

const DetailTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
  color: #111827;
`;

const DetailDescription = styled.p`
  font-size: 14px;
  color: #4b5563;
  margin: 0 0 8px;
  line-height: 1.5;
`;

const DetailCategory = styled.span`
  font-size: 13px;
  color: #6b7280;
  text-transform: capitalize;
`;

const VideoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const VideoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }
`;

const VideoNumber = styled.div`
  width: 32px;
  height: 32px;
  background: #111827;
  color: #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

const VideoInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const VideoTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 2px;
  color: ${(props) => (props.completed ? "#10b981" : "#111827")};
`;

const VideoDesc = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VideoMeta = styled.p`
  font-size: 12px;
  color: #9ca3af;
  margin: 2px 0 0;
`;

const PlayIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #ff0102;
  color: #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
`;

const PlayerTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 12px;
  color: #111827;
`;

const PlayerWrapper = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
`;

const YouTubePlayerContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const VideoPlayer = styled.video`
  width: 100%;
  height: 100%;
  display: block;
`;

const ManualComplete = styled.div`
  margin-top: 16px;
  text-align: center;
`;

const PlayerActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  border: 0;
  border-radius: 8px;
  background: #111827;
  color: #ffffff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 16px;

  &:hover {
    background: #1f2937;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const VideoDescription = styled.p`
  font-size: 14px;
  color: #4b5563;
  margin-top: 16px;
  line-height: 1.5;
`;

const ModalMessage = styled.p`
  margin: 0;
  text-align: center;
  color: #374151;
  font-size: 14px;
  line-height: 1.5;
  padding-bottom: 1rem;
`;

const TriviaSection = styled.div`
  margin-top: 24px;
`;

const TriviaCardBase = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const TriviaCardLocked = styled(TriviaCardBase)`
  background: #f9fafb;
  opacity: 0.7;
`;

const TriviaCardUnlocked = styled(TriviaCardBase)`
  background: #ffffff;
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    border-color: #ff0102;
    box-shadow: 0 2px 8px rgba(255, 1, 2, 0.08);
  }
`;

const TriviaIcon = styled.span`
  font-size: 24px;
  flex-shrink: 0;
`;

const TriviaCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;

  strong {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  span {
    font-size: 13px;
    color: #6b7280;
  }
`;

const TriviaArrow = styled.span`
  font-size: 20px;
  color: #ff0102;
  font-weight: 700;
`;
